import { memo } from 'react';

import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

import { TWlConfig } from '../../interfaces';
import { parseHTML } from '../../services/transformCmsData';
import { isCmsContent } from '../../services/typeGuards';
import type { AppStoreState } from '../../store';
import TenantPhoneLink from '../TenantPhoneLink';

import classes from './Footer.module.css';

type TCmsContent = {
  slug: string;
  modelSlug: string;
  'data-testid': string;
};

function CmsContent({
  slug,
  modelSlug,
  'data-testid': dataTestId,
}: TCmsContent) {
  const content = useSelector(
    (state: AppStoreState) => state.cmsContent?.[modelSlug]
  );

  if (!isCmsContent(content)) {
    return (
      <section data-testid={`${dataTestId}-error`}>
        <p>Content Unavailable</p>
        <p>Please try refreshing the page.</p>
      </section>
    );
  }

  const matchedContent = content?.entries?.find((entry) => entry.slug === slug);
  const html = matchedContent?.fields?.[0]?.text || '';

  return (
    <section className={classes[modelSlug]} data-testid={dataTestId}>
      {parseHTML(html)}
    </section>
  );
}

export type TFooter = {
  cmsContentTestId: string;
  copyrightSlug: string;
  footerSlug: string;
  hasNavItems: boolean;
  hasPersonalInfoLink: boolean;
  hasSocialLinks: boolean;
  overrideClasses?: { root?: string; container?: string; mainWrapper?: string };
  sureFooterLogoSrc?: string;
  tenantPhoneVariant?: TWlConfig['footerConfig']['tenantPhoneVariant'];
};

const contactHref = process.env.REACT_APP_CONTACT_URL;
const claimsHref = process.env.REACT_APP_CLAIMS_URL;
const blogHref = process.env.REACT_APP_BLOG_URL || '/';

function Footer({
  cmsContentTestId,
  copyrightSlug,
  footerSlug,
  hasNavItems,
  hasPersonalInfoLink,
  hasSocialLinks,
  overrideClasses = {},
  sureFooterLogoSrc,
  tenantPhoneVariant,
}: TFooter) {
  return (
    <footer
      className={clsx(classes.root, overrideClasses.root)}
      id="footer"
      data-tenant-phone-variant={tenantPhoneVariant}
      data-testid="footer"
    >
      <div className={clsx(classes.container, overrideClasses.container)}>
        <div className={clsx(classes.mainWrapper, overrideClasses.mainWrapper)}>
          <div className={classes.links}>
            {hasNavItems && (
              <ul data-testid="footer-links" className={classes.linksList}>
                <li
                  data-testid="footer-link-contact"
                  className={classes.linkItem}
                >
                  <a
                    href={contactHref}
                    className={classes.linkItem_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Contact
                  </a>
                </li>
                <li
                  data-testid="footer-link-about-us"
                  className={classes.linkItem}
                >
                  <Link to="/about-us" className={classes.linkItem_link}>
                    About us
                  </Link>
                </li>
                <li
                  data-testid="footer-link-claims"
                  className={classes.linkItem}
                >
                  <a
                    href={claimsHref}
                    className={classes.linkItem_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Claims
                  </a>
                </li>
                <li
                  data-testid="footer-link-terms"
                  className={classes.linkItem}
                >
                  <Link to="/terms" className={classes.linkItem_link}>
                    Terms
                  </Link>
                </li>
                <li
                  data-testid="footer-link-privacy"
                  className={classes.linkItem}
                >
                  <Link to="/privacy" className={classes.linkItem_link}>
                    Privacy
                  </Link>
                </li>
                <li data-testid="footer-link-blog" className={classes.linkItem}>
                  <a
                    href={blogHref}
                    className={classes.linkItem_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            )}

            <div className={classes.topWrapper}>
              {tenantPhoneVariant === 'first-row' && (
                <TenantPhoneLink className={classes.tenantPhone} />
              )}

              {hasSocialLinks && (
                <ul
                  className={classes.socialList}
                  data-testid="footer-social-links"
                >
                  <li className={classes.socialItem}>
                    <a
                      href="https://www.facebook.com/gettoggle"
                      className={classes.socialItem_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-facebook-f" />
                    </a>
                  </li>
                  <li className={classes.socialItem}>
                    <a
                      href="https://www.instagram.com/gettoggle"
                      className={classes.socialItem_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-instagram" />
                    </a>
                  </li>
                </ul>
              )}
            </div>
          </div>

          <div className={classes.info}>
            <div className={classes.wrapper}>
              <div className={classes.innerWrapper}>
                {tenantPhoneVariant === 'second-row' && (
                  <TenantPhoneLink className={classes.tenantPhone} />
                )}

                {!!sureFooterLogoSrc && (
                  <div
                    className={classes.sureBlock}
                    data-testid="footer-sure-block"
                  >
                    <a
                      href="https://www.sureapp.com"
                      rel="noopener noreferrer"
                      className={classes.sureBLock_link}
                      target="_blank"
                    >
                      <img
                        src={sureFooterLogoSrc}
                        alt="Built on Sure logo"
                        className={classes.sureBLock_link_img}
                      />
                    </a>
                  </div>
                )}
              </div>
              {hasPersonalInfoLink && (
                <HashLink
                  data-testid="footer-personal-information"
                  to="/privacy#do-not-sell-my-personal-information"
                  className={classes.personalInformation_link}
                >
                  Do not sell my personal information{' '}
                  <i
                    className={clsx(classes.chevron, 'fas', 'fa-chevron-right')}
                  />
                </HashLink>
              )}
            </div>

            <CmsContent
              modelSlug="copyright"
              slug={copyrightSlug}
              data-testid={`${cmsContentTestId}-copyright`}
            />
            <CmsContent
              modelSlug="footer"
              slug={footerSlug}
              data-testid={cmsContentTestId}
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

export default memo(Footer);
